import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, Star } from "lucide-react";
import { useLang } from "@/i18n";
import { BOOKING_URL, GOOGLE_MAPS_URL, googleReviews, googleScore, reviewScore, reviews } from "@/data/site";
import { Reveal } from "./Reveal";
import { Section } from "./Section";

const allReviews = [
  ...reviews.map((r) => ({ ...r, platform: "Booking.com" })),
  ...googleReviews.map((r) => ({ ...r, platform: "Google" })),
];

const BookingPanel = () => {
  const { t } = useLang();
  return (
    <div data-testid="booking-score-panel" className="flex flex-col rounded-3xl bg-mare p-8 text-sand shadow-xl grain relative overflow-hidden">
      <span className="text-xs font-bold uppercase tracking-[0.28em] text-ochre">Booking.com</span>
      <div className="mt-4 flex items-end gap-3">
        <span className="font-serif text-6xl font-semibold leading-none sm:text-7xl" data-testid="booking-score">{reviewScore.score}</span>
        <span className="pb-1 text-sm text-sand/70">/ 10</span>
      </div>
      <p className="mt-2 font-serif text-xl italic text-ochre">{t.reviews.scoreWord}</p>
      <p className="mt-1 text-sm text-sand/70">{t.reviews.countText}</p>
      <div className="mt-7 space-y-3.5">
        {reviewScore.cats.map((c) => (
          <div key={c.k} data-testid={`score-cat-${c.k}`}>
            <div className="mb-1 flex items-center justify-between text-xs font-semibold">
              <span className="text-sand/85">{t.reviews.cats[c.k]}</span>
              <span className="text-ochre">{String(c.v).replace(".", ",")}</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-sand/15">
              <div className="h-full rounded-full bg-ochre" style={{ width: `${c.v * 10}%` }} />
            </div>
          </div>
        ))}
      </div>
      <a
        data-testid="reviews-booking-link"
        href={BOOKING_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-8 inline-flex items-center justify-center gap-2 rounded-full border border-sand/30 px-5 py-2.5 text-xs font-bold text-sand transition-all duration-200 hover:bg-sand hover:text-mare"
      >
        {t.reviews.readAll} <ArrowRight size={14} />
      </a>
    </div>
  );
};

const GooglePanel = () => {
  const { t } = useLang();
  return (
    <div data-testid="google-score-panel" className="mt-5 rounded-3xl border border-border bg-white p-7 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-sm font-bold tracking-wide text-ink">Google</span>
        <div className="flex gap-0.5 text-ochre" aria-label={`${googleScore.score} / 5`}>
          {[...Array(5)].map((_, s) => <Star key={s} size={15} fill="currentColor" />)}
        </div>
      </div>
      <div className="mt-3 flex items-end gap-2">
        <span className="font-serif text-4xl font-semibold leading-none text-ink" data-testid="google-score">{googleScore.score}</span>
        <span className="pb-0.5 text-sm text-ink-muted">/ 5 · {googleScore.count} {t.reviews.reviewsWord}</span>
      </div>
      <a
        data-testid="reviews-google-link"
        href={GOOGLE_MAPS_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-5 inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-xs font-bold text-mare transition-all duration-200 hover:bg-mare hover:text-sand"
      >
        {t.reviews.mapsLink} <ArrowRight size={14} />
      </a>
    </div>
  );
};

const ReviewCard = ({ r, i }) => {
  const { t } = useLang();
  return (
    <figure data-testid={`review-card-${i}`} className="flex h-full flex-col rounded-2xl border border-border bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-mare font-serif text-lg font-semibold text-sand">
            {r.name.charAt(0)}
          </span>
          <span>
            <span className="block text-sm font-bold text-ink">{r.name}</span>
            <span className="block text-xs text-ink-muted">{r.country}</span>
          </span>
        </div>
        <span className="flex items-center gap-1 rounded-full bg-mare/10 px-2.5 py-1 text-[10px] font-bold text-mare">
          <BadgeCheck size={12} /> {t.reviews.verified}
        </span>
      </div>
      <blockquote className="mt-4 flex-1 font-serif text-base italic leading-relaxed text-ink">“{r.text}”</blockquote>
      <figcaption className="mt-4 text-[11px] font-semibold uppercase tracking-wider text-ink-muted">{r.platform}</figcaption>
    </figure>
  );
};

export const ReviewsSection = ({ all = false }) => {
  const { t } = useLang();
  const list = all ? allReviews : allReviews.slice(0, 3);
  return (
    <section className={all ? "py-16 sm:py-24" : "bg-sand-deep py-16 sm:py-24"} data-testid="reviews-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Section eyebrow={t.reviews.eyebrow} title={t.reviews.title} testid="reviews-heading" />
        <div className="grid items-start gap-6 lg:grid-cols-[340px_1fr]">
          <Reveal>
            <BookingPanel />
            <GooglePanel />
          </Reveal>
          <div className={`grid gap-5 ${all ? "sm:grid-cols-2" : ""}`}>
            {list.map((r, i) => (
              <Reveal key={`${r.platform}-${r.name}`} delay={i * 0.06}>
                <ReviewCard r={r} i={i} />
              </Reveal>
            ))}
          </div>
        </div>
        {!all && (
          <Reveal className="mt-8">
            <Link to="/recensioni" data-testid="reviews-cta" className="inline-flex items-center gap-2 text-sm font-bold text-terracotta transition-colors hover:text-terracotta-dark">
              {t.reviews.cta} <ArrowRight size={15} />
            </Link>
          </Reveal>
        )}
      </div>
    </section>
  );
};
